import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Select, Skeleton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../../styles/theme';
import { DOCK_SCROLLBAR_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import {
  appModalActionsSx, appModalCloseButtonSx, appModalHeaderDividerSx, appModalPaperSx, appModalSecondaryButtonSx,
  appModalTitleBarSx, appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { leadsSelectMenuProps } from '../../Leads/components/leadsUi.styles';
import { useMetaAdPreview } from '../hooks/useMetaAdPreview';
import {
  META_AD_PREVIEW_DEFAULT_SIZE, META_AD_PREVIEW_TIMEOUT_MS, metaAdPreviewFallbackDetail,
} from '../metaAdPreview';
import MetaAdThumb from './MetaAdThumb';
import { metaAdsSkeletonSx } from './metaAdsUi.styles';

const mutedSx = { fontSize: '12px', lineHeight: '16px', color: 'rgba(255,255,255,0.55)' };
const selectSx = {
  minWidth: 240,
  fontSize: '13px',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.85)',
  '& .MuiSelect-select': { py: 0.75, pl: 1.25, pr: '28px !important' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.16)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '&.Mui-disabled .MuiSelect-select': { WebkitTextFillColor: 'rgba(255,255,255,0.45)' },
};
const noticeSx = {
  display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: '8px', width: '100%', boxSizing: 'border-box',
  bgcolor: alpha(colors.warning, 0.08), border: `1px solid ${alpha(colors.warning, 0.22)}`,
};

/**
 * «Ver anuncio» (ajuste 12): la vista previa real de Meta en un iframe dentro de
 * Terrio. Pide `getmetaadpreview` al abrir (se monta con `key={ad.id}`) y al cambiar
 * el formato. Skeleton del tamaño del iframe desde el pedido hasta su `onLoad` (el
 * iframe se monta oculto con `visibility: hidden` para que cargue). Fallback a la miniatura grande con «No pudimos cargar la vista previa
 * de Meta» + «Reintentar» si `available: false`, error, o el iframe no dispara
 * `onLoad` en ~8 s. Ver docs/maps/meta-ads.md § Ver anuncio.
 *
 * @param {{ ad: object, campaignName?: string|null, ownerUserId?: string|null, onClose: () => void }} props
 */
export default function MetaAdPreviewDialog({ ad, campaignName = null, ownerUserId = null, onClose }) {
  const { loading, data, error, formats, format, setFormat, retry } = useMetaAdPreview({ adId: ad.id, ownerUserId });
  const previewUrl = !loading && data?.available ? data.previewUrl : null;
  // Estado del iframe por URL: cargó (`onLoad`) o se pasó del tiempo.
  const [frame, setFrame] = useState({ url: null, loaded: false, timedOut: false });

  useEffect(() => {
    if (!previewUrl) return undefined;
    setFrame({ url: previewUrl, loaded: false, timedOut: false });
    const timer = setTimeout(() => {
      setFrame((f) => (f.url === previewUrl && !f.loaded ? { ...f, timedOut: true } : f));
    }, META_AD_PREVIEW_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [previewUrl]);

  const timedOut = Boolean(previewUrl) && frame.url === previewUrl && frame.timedOut;
  const showFrame = Boolean(previewUrl) && !timedOut;
  const frameLoaded = showFrame && frame.url === previewUrl && frame.loaded;
  const fallback = !loading && !showFrame;
  const width = data?.width || META_AD_PREVIEW_DEFAULT_SIZE.width;
  const height = data?.height || META_AD_PREVIEW_DEFAULT_SIZE.height;
  const thumbUrl = data?.thumbnailUrl || ad.thumbnailUrl || null;
  const isVideo = data ? data.isVideo || ad.isVideo : ad.isVideo;
  const subtitle = ['Anuncio', campaignName].filter(Boolean).join(' · ');

  // Caja con la proporción de Meta (540×690 por defecto), `max-width: 100%`.
  const frameBoxSx = {
    position: 'relative', width: '100%', maxWidth: `${width}px`, aspectRatio: `${width} / ${height}`,
    alignSelf: 'center', borderRadius: '8px', overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.04)',
  };
  // Skeleton del tamaño del iframe: desde el pedido hasta el `onLoad` (no se ve la carga a medias).
  const skeleton = (
    <Skeleton
      variant="rectangular"
      animation="wave"
      aria-label="Cargando la vista previa de Meta"
      data-testid="meta-ad-preview-skeleton"
      sx={{ ...metaAdsSkeletonSx, position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: '8px', transform: 'none' }}
    />
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { ...appModalPaperSx, width: 'min(600px, 95vw)', maxWidth: '600px', height: 'auto', maxHeight: '92vh' },
        'data-testid': 'meta-ad-preview-dialog',
      }}
      aria-labelledby="meta-ad-preview-title"
    >
      <DialogTitle id="meta-ad-preview-title" component="div" sx={appModalTitleBarSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ ...appModalTitleSx, fontSize: '18px', overflowWrap: 'anywhere' }}>{ad.name}</Typography>
          <Typography sx={{ ...mutedSx, mt: 0.5, fontSize: '12.5px' }}>{subtitle}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent sx={{ ...DOCK_SCROLLBAR_SX, px: '24px', pt: 2, pb: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {formats.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography component="label" htmlFor="meta-ad-preview-format" sx={mutedSx}>Formato</Typography>
            <Select
              id="meta-ad-preview-format"
              size="small"
              value={format && formats.some((f) => f.key === format) ? format : formats[0].key}
              onChange={(e) => setFormat(e.target.value)}
              disabled={loading}
              inputProps={{ 'aria-label': 'Formato de la vista previa' }}
              MenuProps={leadsSelectMenuProps}
              sx={{ ...selectSx, alignSelf: 'flex-start' }}
              data-testid="meta-ad-preview-format"
            >
              {formats.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
            </Select>
          </Box>
        ) : null}

        {loading ? (
          <Box sx={frameBoxSx} data-testid="meta-ad-preview-loading" aria-busy="true">{skeleton}</Box>
        ) : null}

        {showFrame ? (
          <Box sx={frameBoxSx} aria-busy={frameLoaded ? undefined : 'true'}>
            {frameLoaded ? null : skeleton}
            <Box
              component="iframe"
              key={previewUrl}
              src={previewUrl}
              title="Vista previa del anuncio"
              width={width}
              height={height}
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer"
              loading="lazy"
              onLoad={() => setFrame((f) => (f.url === previewUrl ? { ...f, loaded: true } : f))}
              data-testid="meta-ad-preview-iframe"
              // Montado pero oculto hasta `onLoad` (sin `display: none`, para que cargue).
              sx={{
                position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: 'block',
                visibility: frameLoaded ? 'visible' : 'hidden',
              }}
            />
          </Box>
        ) : null}

        {fallback ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }} data-testid="meta-ad-preview-fallback">
            <MetaAdThumb url={thumbUrl} isVideo={isVideo} size={280} radius={12} label={`Miniatura de ${ad.name}`} testId="meta-ad-preview-thumb" />
            <Box sx={noticeSx} role="status">
              <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.warning, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', lineHeight: '18px', fontWeight: 600, color: colors.textPanelTitle }}>
                  No pudimos cargar la vista previa de Meta
                </Typography>
                <Typography sx={mutedSx} data-testid="meta-ad-preview-fallback-detail">
                  {metaAdPreviewFallbackDetail({ error, reason: data?.unavailableReason, timedOut })}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={retry}
                sx={{ textTransform: 'none', fontWeight: 600, px: 0.75, minWidth: 0, ml: 'auto', flexShrink: 0, color: colors.primaryLight }}
              >
                Reintentar
              </Button>
            </Box>
          </Box>
        ) : null}

      </DialogContent>
      <DialogActions sx={{ ...appModalActionsSx, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onClose} sx={appModalSecondaryButtonSx}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
